import { Container, Screen, Skeleton, Stack } from '@fluent/ui';
import './HomeSkeleton.css';

/**
 * HomeSkeleton — placeholder view for the very first time progress data
 * is loading after sign-in. Mirrors the real Home layout so there's no
 * jump when content arrives:
 *
 *   - Top bar: XP badge + streak flame ghosts on the left, avatar on the right
 *   - Body: a UnitBanner-shaped card, then 5 serpentine LessonNode ghosts
 *
 * Used only on the first ever load of a session — `useStreak()` has no
 * placeholderData, so its `data === undefined` is the trigger. Subsequent
 * navigations within the session hit the React Query cache and render
 * the real Home immediately.
 *
 * `aria-busy` lets screen readers announce the loading state without
 * reading every shimmer element. Skeleton primitives carry `aria-hidden`
 * already (see @fluent/ui Skeleton).
 */
export function HomeSkeleton() {
  return (
    <Screen aria-busy="true">
      <header className="fluent-home__topbar">
        <div className="fluent-home-skeleton__stats">
          <Skeleton shape="pill" width={72} height={28} />
          <Skeleton shape="pill" width={56} height={28} />
        </div>
        <Skeleton shape="circle" width={36} height={36} />
      </header>

      <Container size="sm">
        <Stack
          gap={8}
          style={{
            padding:
              'var(--space-6, 24px) 0 calc(var(--space-16, 64px) + env(safe-area-inset-bottom))',
          }}
        >
          <Skeleton
            shape="card"
            width="100%"
            height={132}
            className="fluent-home-skeleton__banner"
          />
          <div className="fluent-home-skeleton__nodes" aria-hidden="true">
            {/* 5 nodes — the same 4-cycle serpentine the real tree uses
                so the swap doesn't move the user's eye. */}
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="fluent-home-skeleton__node-slot"
                data-index={i}
              >
                <Skeleton shape="circle" width={72} height={72} />
                <Skeleton width="60%" height={14} className="fluent-home-skeleton__label" />
              </div>
            ))}
          </div>
        </Stack>
      </Container>
    </Screen>
  );
}
